import { Accordion, AccordionDetails, AccordionSummary, Stack, Typography } from '@mui/material';
import { CalculusInfoRow } from './CalculusInfoRow';
import { PriceSuggestionCondition } from '@retail/calculus/types';
import { usePriceSuggestionTFunction } from '@retail/price-suggestion/i18n';
import { CalculusAreaAccordionDetails } from './CalculusAreaAccordionDetails';
import { PriceSuggestionPriority, PriceSuggestionType } from '@retail/products/types';
import { getPriceSuggestionType } from '@retail/products/utils';
import { PriceSuggestionPrice } from '@retail/price-suggestion/types';

interface Props {
  priceSuggestionCondition?: PriceSuggestionCondition;
  priceSuggestionType?: PriceSuggestionType;
  priceSuggestionPrice?: number;
  availablePriceSuggestionItemPrices: PriceSuggestionPrice[];
  editPriceSuggestionPriority?: () => void;
}

export function PriceSuggestionConditionAccordion({
  priceSuggestionCondition,
  priceSuggestionType,
  priceSuggestionPrice,
  availablePriceSuggestionItemPrices,
  editPriceSuggestionPriority,
}: Props) {
  const t = usePriceSuggestionTFunction();

  const activePriority = priceSuggestionCondition?.priceSuggestionPriorities?.find(
    (prio) => prio.priceSuggestionType === priceSuggestionType
  );

  return (
    <Accordion>
      <AccordionSummary>
        <CalculusInfoRow
          description={t('priceSuggestion.recommendedPrice')}
          price={priceSuggestionPrice}
          condition={<PriorityList priorities={activePriority ? [activePriority] : []} />}
          onEditCondition={
            editPriceSuggestionPriority
              ? {
                  disabled: availablePriceSuggestionItemPrices.length === 0,
                  disabledExplanation: t('priceSuggestion.noItemPriceSuggestions'),
                  onEdit: editPriceSuggestionPriority,
                }
              : undefined
          }
          isActivePrice={!!priceSuggestionType}
        />
      </AccordionSummary>
      <AccordionDetails>
        {priceSuggestionCondition ? (
          <CalculusAreaAccordionDetails
            bindingType={priceSuggestionCondition.bindingType}
            conditionValue={
              <PriorityList
                priorities={priceSuggestionCondition.priceSuggestionPriorities ?? []}
                activePriceType={priceSuggestionType}
              />
            }
            conditionValueDesc={t('priceSuggestion.priority')}
            validFrom={priceSuggestionCondition.validFrom}
            validTo={priceSuggestionCondition.validTo}
          />
        ) : (
          <Typography variant="body2" sx={{ pl: 2 }}>
            {t('priceSuggestion.noActivePriceSuggestion')}
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

interface PriorityProps {
  priorities: PriceSuggestionPriority[];
  activePriceType?: PriceSuggestionType;
}

const PriorityList = ({ priorities, activePriceType }: PriorityProps) => {
  const t = usePriceSuggestionTFunction();
  return (
    <Stack pr={0.5}>
      {priorities.map(({ priority, priceSuggestionType }) => {
        return (
          <Stack
            key={priority}
            flexDirection="row"
            alignItems="center"
            gap={1}
            px={1}
            bgcolor={priceSuggestionType === activePriceType ? 'success.100' : 'inherit'}
          >
            <Typography variant="subtitle2" color="textSecondary">
              {priority}.
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {getPriceSuggestionType(t, priceSuggestionType)}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
};
